import React from 'react';
import { motion } from 'framer-motion';
import { Timer, Plus, AlertCircle } from 'lucide-react';

interface TalkTimeAlertProps {
  minutes: number;
  onPurchase: () => void;
}

export function TalkTimeAlert({ minutes, onPurchase }: TalkTimeAlertProps) {
  const isLow = minutes < 1;
  const isDepleted = minutes === 0;

  const getMessage = () => {
    if (isDepleted) {
      return "Hi there! I'd love to continue our conversation, but we're out of talk time. Would you like to add more time so we can keep chatting?";
    }
    if (isLow) {
      return "Just to let you know, we have less than a minute left in our session. I'd hate for our conversation to be cut short - shall we add more time?";
    }
    return "Hi! I notice we have about 2 minutes left in our session. Would you like to extend our time together?";
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`fixed bottom-24 right-6 z-50 max-w-sm ${isLow ? 'animate-pulse' : ''}`}
    >
      <div className={`rounded-lg p-4 backdrop-blur-sm border ${
        isLow 
          ? 'bg-warning/10 border-warning/20 text-warning' 
          : 'bg-primary/10 border-primary/20 text-primary'
      }`}>
        <div className="flex items-center gap-3">
          {isLow ? (
            <AlertCircle className="w-5 h-5 shrink-0" />
          ) : (
            <Timer className="w-5 h-5 shrink-0" />
          )}
          <div className="flex-1">
            <div className="font-medium">
              {isDepleted ? 'Session Ended' : 'Talk Time Running Low'}
            </div>
            <p className="text-sm opacity-80">
              {getMessage()}
            </p>
          </div>
          <button
            onClick={onPurchase}
            className={`shrink-0 p-2 rounded-lg transition-colors flex items-center gap-1 ${
              isLow 
                ? 'bg-warning/20 hover:bg-warning/30' 
                : 'bg-primary/20 hover:bg-primary/30'
            }`}
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm">Add Time</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
}